// ============================================================================
// client/porte-maillet.ts — LA POSTURE DU PORTEUR DE MAILLET (06/08)
// ----------------------------------------------------------------------------
// Retour de Claude-Alain sur la première série de prises : « s'il tient le
// maillet sur l'épaule il doit tenir le manche en avant. Il lui faut une
// nouvelle animation voire une nouvelle position du personnage quand il tient
// le maillet au lieu de la pioche. La solution sur l'épaule ne me convainc pas. »
//
// CE QUI CLOCHAIT : je ne faisais que faire PIVOTER le maillet à côté d'un
// personnage qui, lui, ne changeait pas d'un pixel. Or porter vingt kilos
// d'acier change tout un corps — c'est le CORPS qui doit dire le poids.
//
// Le pantin (papier.ts) est heureusement paramétrique jusqu'aux coudes :
// épaules, coudes, buste, bassin, tête, foulard. On peut donc écrire de VRAIES
// postures de porteur, pas seulement replacer un accessoire. C'est ce que fait
// ce fichier : quatre candidates, chacune = une pose DEBOUT + une pose EN
// MARCHE + l'ancrage du maillet dans cette posture.
//
// Le jeu peint `PORTE_CHOISIE`. Un seul nombre à changer.
// ============================================================================

import type { Pose } from './papier'

export type PorteMaillet = 0 | 1 | 2 | 3
export const PORTE_NOMS = [
  "L'épaulée, manche en avant",
  'La garde à deux mains',
  'Le contrepoids',
  "L'appui au sol",
] as const

/** Ce que le JEU peint. Un seul nombre.
 *  06/08 : Claude-Alain a choisi **A3, LE CONTREPOIDS** — « la direction me paraît bonne,
 *  continue à développer l'idée et les animations, son et autre ». */
export const PORTE_CHOISIE: PorteMaillet = 2

export interface PosePorteur {
  debout: Pose      // à l'arrêt
  marche: Pose[]    // le cycle de marche (les jambes courent, le haut du corps porte)
  dx: number        // ancrage du maillet, en fraction de case, depuis le CENTRE du sprite
  dy: number        // ... et depuis le HAUT de la case
  ang: number       // rotation du maillet (dessiné debout, poigne en 0,0, tête en haut)
  taille: number    // longueur du maillet en fraction de case
  // L'INERTIE (06/08, développement du contrepoids) : la tête d'acier ne suit pas le corps au
  // même instant — elle RETARDE, part en arrière quand il accélère, revient quand il s'arrête.
  // C'est ce décalage, et lui seul, qui fait sentir la masse. Exprimé en radians d'amplitude.
  balanceMarche: number // battement du maillet au rythme des pas
  balanceRepos: number  // respiration à l'arrêt : il compense en permanence, il ne tient pas immobile
}

// Les jambes de course du jeu, recopiées telles quelles : le porteur court
// pareil, c'est le HAUT du corps qui change. (Reprendre les valeurs plutôt que
// les importer garde chaque posture lisible d'un seul coup d'œil.)
const JAMBES = [
  { bassinY: 0, bassinRot: -.1, cuAV: -.72, gnAV: .12, cuAR: .58, gnAR: .78 },
  { bassinY: 2.5, bassinRot: -.04, cuAV: -.22, gnAV: .34, cuAR: .42, gnAR: 1.35, solW: 0.55 },
  { bassinY: -3.5, bassinRot: .04, cuAV: .5, gnAV: 1.25, cuAR: -.68, gnAR: .55, solW: 0 },
  { bassinY: 0, bassinRot: .1, cuAV: .58, gnAV: .78, cuAR: -.72, gnAR: .12 },
  { bassinY: 2.5, bassinRot: .04, cuAV: .42, gnAV: 1.35, cuAR: -.22, gnAR: .34, solW: 0.55 },
  { bassinY: -3.5, bassinRot: -.04, cuAV: -.68, gnAV: .55, cuAR: .5, gnAR: 1.25, solW: 0 },
]

/** Compose le cycle de marche : les jambes du jeu + le haut du corps de la posture. */
function marcher(haut: Partial<Pose>, nom: string): Pose[] {
  return JAMBES.map((j, i) => ({
    nom: `${nom} · marche ${i}`,
    ...j,
    buste: [(haut.buste as number[])[0]!, j.bassinY ?? 0, (haut.buste as number[])[2]!],
    tete: [(haut.tete as number[])[0]!, (j.bassinY ?? 0) * 0.8, (haut.tete as number[])[2]!],
    epAV: haut.epAV as number, cdAV: haut.cdAV as number,
    epAR: haut.epAR as number, cdAR: haut.cdAR as number,
    foulard: (haut.foulard as number ?? 0.4) + (i % 3 === 2 ? 0.5 : 0),
  } as Pose))
}

/* ─── 0 · L'ÉPAULÉE, MANCHE EN AVANT — sa correction, faite pour de vrai ────
   La tête d'acier repose DERRIÈRE, sur l'épaule ; les deux mains tiennent le
   manche EN AVANT du torse. Le buste est légèrement renversé en arrière : c'est
   ce contrepoids qui dit le poids. Bras avant tendu vers l'avant-bas (il tient
   le bout du manche), bras arrière replié haut (il cale le manche à l'épaule). */
const HAUT_0 = { buste: [-2, 0, -.1], tete: [-1, 0, .06], epAV: -.55, cdAV: -.85, epAR: -1.35, cdAR: -1.15, foulard: .3 }

/* ─── 1 · LA GARDE À DEUX MAINS — le maillet en travers, prêt à balayer ─────
   Manche à l'oblique devant le corps, tête basse en avant, les deux mains
   dessus. Buste penché en avant, genoux fléchis : une posture de garde. */
const HAUT_1 = { buste: [3, 0, .2], tete: [3, 0, -.1], epAV: .55, cdAV: -.95, epAR: .2, cdAR: -.75, foulard: .45 }

/* ─── 2 · LE CONTREPOIDS — le poids le tire en arrière, et ça se voit ───────
   Il marche le buste renversé, la tête du maillet haute derrière lui, une seule
   main au bout du manche tendue vers l'avant. La plus expressive des quatre. */
const HAUT_2 = { buste: [-4, 0, -.2], tete: [-3, 0, .12], epAV: -.95, cdAV: -.5, epAR: -1.9, cdAR: -.9, foulard: .25 }

/* ─── 3 · L'APPUI AU SOL — le guerrier qui se repose sur son marteau ────────
   Tête d'acier POSÉE au sol devant lui, mains empilées sur le haut du manche,
   buste légèrement penché en avant. Immobile, c'est la plus imposante ; en
   marche, le maillet se relève (sinon il laboure le décor). */
const HAUT_3 = { buste: [2, 0, .12], tete: [2, 0, -.06], epAV: -1.25, cdAV: -.35, epAR: -1.05, cdAR: -.45, foulard: .2 }

export const PORTEURS: readonly PosePorteur[] = [
  {
    debout: { nom: 'épaulée', bassinY: 0, bassinRot: -.04, ...HAUT_0, cuAV: .16, gnAV: .08, cuAR: -.2, gnAR: .14 } as Pose,
    marche: marcher(HAUT_0, 'épaulée'),
    dx: .30, dy: .46, ang: -1.45, taille: .80, balanceMarche: .10, balanceRepos: .03, // la tête passe DERRIÈRE l'épaule, jamais sur le visage
  },
  {
    debout: { nom: 'garde', bassinY: 2, bassinRot: 0, ...HAUT_1, cuAV: -.3, gnAV: .5, cuAR: .28, gnAR: .55 } as Pose,
    marche: marcher(HAUT_1, 'garde'),
    dx: -.04, dy: .48, ang: 2.36, taille: .78, balanceMarche: .08, balanceRepos: .025, // les mains au centre du torse, la tête basse EN AVANT
  },
  {
    debout: { nom: 'contrepoids', bassinY: 0, bassinRot: -.08, ...HAUT_2, cuAV: .22, gnAV: .1, cuAR: -.26, gnAR: .18 } as Pose,
    marche: marcher(HAUT_2, 'contrepoids'),
    dx: .30, dy: .62, ang: -0.58, taille: .86, balanceMarche: .20, balanceRepos: .055, // main basse en avant, tête HAUTE derrière : le poids tire
  },
  {
    debout: { nom: 'appui', bassinY: 1, bassinRot: .04, ...HAUT_3, cuAV: .1, gnAV: .12, cuAR: -.16, gnAR: .16 } as Pose,
    marche: marcher(HAUT_3, 'appui'),
    dx: .26, dy: .12, ang: 3.14, taille: .86, balanceMarche: .06, balanceRepos: .015, // mains empilées en haut, tête POSÉE au sol devant
  },
]
